import { Button, Flex, Text } from '@radix-ui/themes'
import { CarIcon, CreditCardIcon, FileSpreadsheetIcon, SigmaIcon, TrendingUpIcon } from 'lucide-react'

import { AnnouncementBody, useJourney } from '../../journey'

import { marginReportSpec } from './journey'
import { Simulator } from './Simulator'

import type { AnnouncementProps } from '../types'
import type { MarginReportSpotlight } from './journey'

const POINTS: { id: Exclude<MarginReportSpotlight, null>; icon: typeof CarIcon; title: string; body: string }[] = [
  {
    id: 'vehiculo',
    icon: CarIcon,
    title: 'Margen Vehículo',
    body: 'La utilidad de la compra y venta del vehículo, sin mezclar el financiamiento.',
  },
  {
    id: 'comision',
    icon: CreditCardIcon,
    title: 'Comisión de Crédito',
    body: 'Lo que gana cuando la venta se cierra con crédito, en su propia columna. Sin crédito, verá $0.',
  },
  {
    id: 'total',
    icon: SigmaIcon,
    title: 'Margen Total',
    body: 'La suma de ambos. El mismo total que ya conocía, ahora transparente.',
  },
]

export const Body = ({ active, onDismiss }: AnnouncementProps) => {
  const journey = useJourney(marginReportSpec, active)

  return (
    <AnnouncementBody
      heading="Del margen único al desglose"
      blurb="El recorrido completo: entrar a Reportes, abrir la pestaña Margen y ver el desglose de cada negocio."
      caption={journey.caption}
      stage={<Simulator state={journey} />}
      story={
        <>
          <Flex align="center" gap="2" className="mb-2 text-[#107c41]">
            <TrendingUpIcon className="h-4 w-4" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-wider">Reportes · Pestaña Margen</span>
          </Flex>

          <p className="text-[15px] font-bold leading-snug text-[#1c1917]">
            Cuánto gana por el auto y cuánto por el crédito
          </p>
          <p className="mt-1 text-xs leading-relaxed text-[#706d71]">
            El Reporte de Márgenes separa la utilidad propia del vehículo de la comisión que recibe por el
            financiamiento. El total es el mismo de siempre; la diferencia es que ahora sabe exactamente de dónde viene.
          </p>

          <div className="mt-3.5 space-y-2">
            {POINTS.map((point) => {
              const Icon = point.icon

              return (
                <div
                  key={point.id}
                  className="vk-info-block rounded-r py-2 pl-2.5 pr-2"
                  data-active={journey.spotlight === point.id}
                >
                  <Flex align="start" gap="2">
                    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#107c41]" />
                    <div>
                      <p className="text-[11px] font-bold leading-tight text-[#1c1917]">{point.title}</p>
                      <p className="text-[11px] leading-snug text-[#706d71]">{point.body}</p>
                    </div>
                  </Flex>
                </div>
              )
            })}
          </div>

          <Flex align="start" gap="2" className="mt-3 rounded-lg border border-[#e4e1e4] bg-[#faf8f8] p-2.5">
            <FileSpreadsheetIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#107c41]" />
            <p className="text-[11px] leading-snug text-[#706d71]">
              La planilla de Excel que descarga desde la pestaña incluye las mismas tres columnas, lista para sus
              propios análisis.
            </p>
          </Flex>

          <Flex justify="end" className="mt-auto pt-4">
            <Button size="2" color="green" onClick={onDismiss}>
              <Text weight="bold">Entendido</Text>
            </Button>
          </Flex>
        </>
      }
    />
  )
}
